import { Component, h, Prop, State, Element, Listen, Watch, Build } from '@stencil/core';
import { debounce } from '../../../../utils/helpers/debounce';

// Popular email domains for autocomplete
const EMAIL_DOMAINS = ['gmail.com', 'yahoo.com', 'aol.com', 'hotmail.com', 'outlook.com'];

// Get form action URL
const getFormAction = () => {
  return 'https://snseats.signnow.com/purchase/business_free_trial/loggedout';
};

// Check if current domain is signnow.com
const isSignNowDomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('signnow.com') || hostname.includes('mrkhub.com');
};

// Cookie helper function
const setCookie = (name: string, value: string, options: { domain?: string; path?: string; expires?: number } = {}) => {
  let cookieString = `${name}=${encodeURIComponent(value)}`;

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  } else {
    cookieString += `; path=/`;
  }

  // Set expiration to 1 year if not specified
  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  } else {
    const date = new Date();
    date.setTime(date.getTime() + 365 * 24 * 60 * 60 * 1000); // 1 year
    cookieString += `; expires=${date.toUTCString()}`;
  }

  document.cookie = cookieString;
};

// Set abdestination cookie for .signnow.com domain (only if on signnow.com domain)
const setAbdestinationCookie = () => {
  if (isSignNowDomain()) {
    setCookie('abdestination', 'sn_herald', { domain: '.signnow.com', path: '/' });
  }
};

/**
 * Inline email-and-submit form for free trial signup with autocomplete email domain suggestions.
 * Single row with email input and submit button. Shows popular email provider suggestions while typing.
 * Product-specific form that submits to an external signup URL and sets tracking cookies.
 *
 * @example
 *   <!-- With custom validation messages for the email field -->
 *   <!-- required-message: shown when the field is empty; invalid-message: shown for a malformed email -->
 *   <form-free-trial-type-1
 *     required-message="Please enter your email"
 *     invalid-message="This value seems to be invalid"
 *   ></form-free-trial-type-1>
 */
@Component({
  tag: 'form-free-trial-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class FormFreeTrialType1 {
  /**
   * Component constants object containing all required text constants
   */
  @Prop() constants?: Record<string, string>;

  // Placeholder in the Free Trial Form email input
  @Prop() inputPlaceholder: string = 'Your email';
  // Caption for the Free Trial Form submit button
  @Prop() submitText: string = 'Start your free trial';
  // Note text displayed below the submit button (e.g., "No credit card required")
  @Prop() note?: string;
  // Button variant (primary, secondary, tertiary, ghost)
  @Prop() buttonVariant: 'primary' | 'secondary' | 'tertiary' | 'ghost' = 'primary';
  // Mobile layout type: 'inline' keeps elements in one row, 'stacked' puts them in column
  @Prop() mobileLayout: 'inline' | 'stacked' = 'inline';
  // Error message shown when the email field is left EMPTY (required validation).
  // When omitted, the default "field required" message is used.
  @Prop() requiredMessage?: string;
  // Error message shown when a value IS entered but is not a valid email address.
  // When omitted, the default "invalid email" message is used.
  @Prop() invalidMessage?: string;
  // Minimum allowed length of the email value. Leave empty (0) to disable the check.
  @Prop() minLength?: number;
  // Maximum allowed length of the email value. Leave empty (0) to disable the check.
  @Prop() maxLength?: number;
  // Error message shown when the entered value is SHORTER than `minLength`.
  // Only fires when `minLength` is set. When omitted, a default is used.
  @Prop() minLengthMessage?: string;
  // Error message shown when the entered value is LONGER than `maxLength`.
  // Only fires when `maxLength` is set. When omitted, a default is used.
  @Prop() maxLengthMessage?: string;

  @State() showAutocomplete: boolean = false;
  @State() selectedIndex: number = -1;
  @State() emailValue: string = '';
  @State() autocompletePosition: 'bottom' | 'top' = 'bottom';
  @State() positionClass: 'form-free-trial-type-1__autocomplete--top' | 'form-free-trial-type-1__autocomplete--bottom' | '' = '';

  @Element() hostEl!: HTMLElement;

  @Watch('showAutocomplete')
  handleShowAutocompleteChange(newValue: boolean) {
    if (!Build.isBrowser) return;
    if (newValue) {
      // Ensure element has initial hidden state before clearing inline styles
      if (this.autocompleteRef) {
        // Set initial state first
        this.autocompleteRef.style.opacity = '0';
        this.autocompleteRef.style.visibility = 'hidden';
        this.autocompleteRef.style.pointerEvents = 'none';
        this.autocompleteRef.style.transform = 'translateY(-10px)';

        // Use double requestAnimationFrame to ensure browser applies initial state before clearing
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.autocompleteRef && this.showAutocomplete) {
              this.autocompleteRef.style.opacity = '';
              this.autocompleteRef.style.visibility = '';
              this.autocompleteRef.style.pointerEvents = '';
              this.autocompleteRef.style.transform = '';
            }
          });
        });
      }

      // Calculate position before showing
      requestAnimationFrame(() => {
        this.updateAutocompletePosition();
      });
      this.setupPositionTracking();
    } else {
      this.cleanupPositionTracking();
      // Force immediate hiding with inline styles
      if (this.autocompleteRef) {
        this.autocompleteRef.style.opacity = '0';
        this.autocompleteRef.style.visibility = 'hidden';
        this.autocompleteRef.style.pointerEvents = 'none';
        this.autocompleteRef.style.transform = 'translateY(-10px)';
      }
      this.positionClass = '';
    }
  }

  private inputRef?: HTMLInputElement;
  private autocompleteRef?: HTMLDivElement;
  private optionRefs: HTMLDivElement[] = [];
  private resizeObserver?: ResizeObserver;
  private debouncedUpdatePosition?: () => void;

  private inputListenersAttached: boolean = false;

  // Update input reference and attach event listeners
  private updateInputRef() {
    if (!Build.isBrowser) return;
    const inputWrapper = this.hostEl.querySelector('.input-wrapper');
    if (inputWrapper) {
      const input = inputWrapper.querySelector('input');
      if (input) {
        // If input changed or listeners not attached yet
        if (input !== this.inputRef || !this.inputListenersAttached) {
          // Remove old listener if exists
          if (this.inputRef && this.inputListenersAttached) {
            this.inputRef.removeEventListener('input', this.handleInputChange);
            this.inputRef.removeEventListener('keydown', this.handleInputKeyDown);
            this.inputListenersAttached = false;
          }

          this.inputRef = input;

          // Add new listeners only if not already attached
          // Use capture phase for keydown to intercept before other handlers
          if (!this.inputListenersAttached) {
            this.inputRef.addEventListener('input', this.handleInputChange);
            this.inputRef.addEventListener('keydown', this.handleInputKeyDown, true);
            this.inputListenersAttached = true;
          }

          // Set ARIA attributes for accessibility
          this.updateInputAriaAttributes();
        } else {
          // Update ARIA attributes if input already exists and listeners are attached
          this.updateInputAriaAttributes();
        }
      }
    }
  }

  // Update ARIA attributes for accessibility
  private updateInputAriaAttributes() {
    if (!this.inputRef) return;

    this.inputRef.setAttribute('role', 'combobox');
    this.inputRef.setAttribute('aria-expanded', this.showAutocomplete ? 'true' : 'false');
    this.inputRef.setAttribute('aria-autocomplete', 'list');
    this.inputRef.setAttribute('aria-haspopup', 'listbox');

    if (this.showAutocomplete) {
      this.inputRef.setAttribute('aria-controls', 'email-autocomplete-listbox');
      if (this.selectedIndex >= 0) {
        this.inputRef.setAttribute('aria-activedescendant', `autocomplete-option-${this.selectedIndex}`);
      } else {
        this.inputRef.removeAttribute('aria-activedescendant');
      }
    } else {
      this.inputRef.removeAttribute('aria-controls');
      this.inputRef.removeAttribute('aria-activedescendant');
    }
  }

  // Handle input changes
  private handleInputChange = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.emailValue = value;

    // Hide autocomplete if input is empty
    if (!value || value.trim() === '') {
      this.showAutocomplete = false;
      this.updateInputAriaAttributes();
      return;
    }

    // Check if @ symbol is present and there's text before it
    const atIndex = value.lastIndexOf('@');
    if (atIndex > 0 && atIndex < value.length - 1) {
      // User has typed something before @ and possibly after
      const domainPart = value.substring(atIndex + 1);
      // Show autocomplete if domain part doesn't contain a dot (incomplete domain)
      if (!domainPart.includes('.')) {
        this.showAutocomplete = true;
        this.selectedIndex = -1;
        this.optionRefs = [];
        requestAnimationFrame(() => {
          this.updateAutocompletePosition();
          this.setupPositionTracking();
        });
        this.updateInputAriaAttributes();
      } else {
        this.showAutocomplete = false;
        this.selectedIndex = -1;
        this.optionRefs = [];
        this.cleanupPositionTracking();
        this.positionClass = '';
        this.updateInputAriaAttributes();
      }
    } else if (atIndex === value.length - 1) {
      // User just typed @, show autocomplete
      this.showAutocomplete = true;
      this.selectedIndex = -1;
      this.optionRefs = [];
      requestAnimationFrame(() => {
        this.updateAutocompletePosition();
        this.setupPositionTracking();
      });
      this.updateInputAriaAttributes();
    } else {
      this.showAutocomplete = false;
      this.selectedIndex = -1;
      this.optionRefs = [];
      this.cleanupPositionTracking();
      this.positionClass = '';
      this.updateInputAriaAttributes();
    }
  };

  private isProcessingKeyDown: boolean = false;

  // Handle keyboard navigation in autocomplete
  private handleInputKeyDown = (event: KeyboardEvent) => {
    if (!this.showAutocomplete) return;

    const filteredDomains = this.getFilteredDomains();
    if (filteredDomains.length === 0) return;

    // Prevent double processing
    if (this.isProcessingKeyDown) return;

    // Only handle arrow keys, Enter, Tab, Escape, Home, and End when autocomplete is visible
    if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape', 'Home', 'End'].includes(event.key)) {
      return;
    }

    this.isProcessingKeyDown = true;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const currentIndex = this.selectedIndex;
    let newIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        // If no item is selected (index -1), select first item (index 0)
        // If at last item, wrap to first item (circular navigation)
        // Otherwise move to next item
        if (currentIndex < 0) {
          newIndex = 0;
        } else if (currentIndex >= filteredDomains.length - 1) {
          newIndex = 0; // Wrap to first item
        } else {
          newIndex = currentIndex + 1;
        }
        break;
      case 'ArrowUp':
        // If no item is selected (index -1), select last item
        // If at first item (index 0), wrap to last item (circular navigation)
        // Otherwise move to previous item
        if (currentIndex < 0) {
          newIndex = filteredDomains.length - 1;
        } else if (currentIndex === 0) {
          newIndex = filteredDomains.length - 1; // Wrap to last item
        } else {
          newIndex = currentIndex - 1;
        }
        break;
      case 'Enter':
        event.preventDefault();
        if (currentIndex >= 0 && currentIndex < filteredDomains.length) {
          this.selectDomain(filteredDomains[currentIndex]);
          this.isProcessingKeyDown = false;
          return;
        }
        // If no item selected, close autocomplete
        this.showAutocomplete = false;
        this.selectedIndex = -1;
        this.cleanupPositionTracking();
        this.positionClass = '';
        this.updateInputAriaAttributes();
        break;
      case 'Tab':
        if (currentIndex >= 0 && currentIndex < filteredDomains.length) {
          this.selectDomain(filteredDomains[currentIndex]);
          this.isProcessingKeyDown = false;
          return;
        }
        break;
      case 'Escape':
        event.preventDefault();
        this.showAutocomplete = false;
        newIndex = -1;
        this.optionRefs = [];
        this.cleanupPositionTracking();
        this.positionClass = '';
        this.updateInputAriaAttributes();
        break;
      case 'Home':
        if (filteredDomains.length > 0) {
          event.preventDefault();
          newIndex = 0;
        }
        break;
      case 'End':
        if (filteredDomains.length > 0) {
          event.preventDefault();
          newIndex = filteredDomains.length - 1;
        }
        break;
    }

    // Update selected index only if it changed
    if (newIndex !== currentIndex) {
      this.selectedIndex = newIndex;
      this.scrollToSelectedOption();
      this.updateInputAriaAttributes();
    }

    // Reset flag after a short delay to allow next keypress
    setTimeout(() => {
      this.isProcessingKeyDown = false;
    }, 0);
  };

  // Scroll to selected option to keep it visible
  private scrollToSelectedOption = () => {
    if (this.selectedIndex < 0 || !this.autocompleteRef) return;

    requestAnimationFrame(() => {
      if (!this.autocompleteRef || this.selectedIndex < 0) return;

      const selectedOption = this.optionRefs[this.selectedIndex];
      if (selectedOption) {
        const container = this.autocompleteRef;
        const scrollTop = container.scrollTop;
        const optionOffsetTop = selectedOption.offsetTop;
        const optionHeight = selectedOption.offsetHeight;
        const containerHeight = container.clientHeight;

        // Check if option is above visible area
        if (optionOffsetTop < scrollTop) {
          container.scrollTop = optionOffsetTop;
        }
        // Check if option is below visible area
        else if (optionOffsetTop + optionHeight > scrollTop + containerHeight) {
          container.scrollTop = optionOffsetTop + optionHeight - containerHeight;
        }
      }
    });
  };

  // Update autocomplete dropdown position
  private updateAutocompletePosition = () => {
    if (!Build.isBrowser || !this.inputRef || !this.autocompleteRef) return;

    requestAnimationFrame(() => {
      if (!this.inputRef || !this.autocompleteRef) return;

      const inputRect = this.inputRef.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - inputRect.bottom;
      const spaceAbove = inputRect.top;

      const MAX_DROPDOWN_HEIGHT = 240;
      const PADDING = 4;
      const MIN_DROPDOWN_HEIGHT = 80;

      const availableBelow = Math.max(spaceBelow - PADDING, MIN_DROPDOWN_HEIGHT);
      const availableAbove = Math.max(spaceAbove - PADDING, MIN_DROPDOWN_HEIGHT);

      // Estimate dropdown height based on filtered domains
      const filteredDomains = this.getFilteredDomains();
      const estimatedHeight = Math.min(filteredDomains.length * 44 + 16, MAX_DROPDOWN_HEIGHT);
      const fitsBelow = estimatedHeight <= availableBelow;
      const fitsAbove = estimatedHeight <= availableAbove;
      const shouldShowBelow = fitsBelow || (!fitsAbove && availableBelow >= availableAbove);

      const maxHeight = Math.min(shouldShowBelow ? availableBelow : availableAbove, MAX_DROPDOWN_HEIGHT);
      this.autocompleteRef.style.maxHeight = `${maxHeight}px`;

      // Update position class
      const newPositionClass = shouldShowBelow ? 'form-free-trial-type-1__autocomplete--bottom' : 'form-free-trial-type-1__autocomplete--top';

      if (this.positionClass !== newPositionClass) {
        this.positionClass = newPositionClass;
      }

      // Update position
      const outerWrapper = this.hostEl.querySelector('.form-free-trial-type-1__input-wrapper') as HTMLElement;
      if (outerWrapper) {
        const outerWrapperRect = outerWrapper.getBoundingClientRect();
        const topOffset = inputRect.bottom - outerWrapperRect.top;
        const bottomOffset = outerWrapperRect.bottom - inputRect.top;

        if (shouldShowBelow) {
          this.autocompletePosition = 'bottom';
          this.autocompleteRef.style.top = `${topOffset}px`;
          this.autocompleteRef.style.bottom = 'auto';
        } else {
          this.autocompletePosition = 'top';
          this.autocompleteRef.style.top = 'auto';
          this.autocompleteRef.style.bottom = `${bottomOffset}px`;
        }
      }
    });
  };

  private setupPositionTracking() {
    if (!Build.isBrowser || !this.showAutocomplete) return;

    // Create debounced version of updateAutocompletePosition
    this.debouncedUpdatePosition = debounce(this.updateAutocompletePosition, 100);

    // Track window resize
    window.addEventListener('resize', this.debouncedUpdatePosition);
    window.addEventListener('scroll', this.debouncedUpdatePosition, true);

    // Track parent resize
    if (window.ResizeObserver) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.debouncedUpdatePosition) {
          this.debouncedUpdatePosition();
        }
      });
      this.resizeObserver.observe(this.hostEl);
    }
  }

  private cleanupPositionTracking() {
    if (this.debouncedUpdatePosition) {
      window.removeEventListener('resize', this.debouncedUpdatePosition);
      window.removeEventListener('scroll', this.debouncedUpdatePosition, true);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
  }

  // Select domain from autocomplete
  private selectDomain(domain: string) {
    if (!this.inputRef) return;

    const value = this.emailValue;
    const atIndex = value.lastIndexOf('@');

    if (atIndex >= 0) {
      const beforeAt = value.substring(0, atIndex + 1);
      const newValue = beforeAt + domain;

      this.emailValue = newValue;
      this.inputRef.value = newValue;

      // Trigger input event to update me-input component
      const inputEvent = new Event('input', { bubbles: true });
      this.inputRef.dispatchEvent(inputEvent);

      // Trigger change event
      const changeEvent = new Event('change', { bubbles: true });
      this.inputRef.dispatchEvent(changeEvent);
    }

    this.showAutocomplete = false;
    this.selectedIndex = -1;
    this.optionRefs = [];
    this.cleanupPositionTracking();
    this.positionClass = '';
    this.updateInputAriaAttributes();
    this.inputRef.focus();
  }

  // Handle click outside to close autocomplete
  @Listen('click', { target: 'window' })
  handleClickOutside(event: MouseEvent) {
    if (!Build.isBrowser || !this.showAutocomplete) return;

    const target = event.target as HTMLElement;
    if (this.inputRef && this.autocompleteRef && !this.inputRef.contains(target) && !this.autocompleteRef.contains(target)) {
      this.showAutocomplete = false;
      this.selectedIndex = -1;
      this.optionRefs = [];
      this.cleanupPositionTracking();
      this.positionClass = '';
      this.updateInputAriaAttributes();
    }
  }

  // Hide autocomplete when form is submitted to show validation errors
  @Listen('submit')
  handleFormSubmit() {
    if (!Build.isBrowser) return;
    this.showAutocomplete = false;
    this.selectedIndex = -1;
    this.optionRefs = [];
    this.cleanupPositionTracking();
    this.positionClass = '';
  }

  // Get filtered domains based on current input
  private getFilteredDomains(): string[] {
    const value = this.emailValue;
    const atIndex = value.lastIndexOf('@');

    if (atIndex < 0) return EMAIL_DOMAINS;

    const domainPart = value.substring(atIndex + 1).toLowerCase();
    if (!domainPart) return EMAIL_DOMAINS;

    return EMAIL_DOMAINS.filter(domain => domain.toLowerCase().startsWith(domainPart));
  }

  // Custom submit handler to set cookie and redirect with GET parameters
  private handleSubmit = async (data: Record<string, unknown>) => {
    if (!Build.isBrowser) return;
    // Set abdestination cookie before submission (only if on signnow.com domain)
    setAbdestinationCookie();

    // Save email to cookie if present (only if on signnow.com domain)
    const email = data.email;
    if (email && typeof email === 'string' && email.trim() && isSignNowDomain()) {
      const cookieName = 'SN_SIGNUP_USER_EMAIL';
      // Set cookie for .signnow.com domain
      setCookie(cookieName, email.trim(), { domain: '.signnow.com', path: '/' });
    }

    // Build query string for GET request
    const params = new URLSearchParams();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, String(value));
      }
    });

    const actionUrl = getFormAction();
    const url = `${actionUrl}?${params.toString()}`;

    // Small delay to ensure cookie is set before redirect
    await new Promise(resolve => setTimeout(resolve, 10));

    // Redirect to the URL (GET request via browser navigation)
    if (Build.isBrowser) {
      window.location.href = url;
    }
  };

  componentDidLoad() {
    if (!Build.isBrowser) return;
    // Find the input element after component loads
    setTimeout(() => {
      this.updateInputRef();
    }, 0);
  }

  componentDidUpdate() {
    if (!Build.isBrowser) return;
    // Update input ref after each render
    setTimeout(() => {
      this.updateInputRef();
      // Update autocomplete position after render if it's shown
      if (this.showAutocomplete && this.autocompleteRef) {
        requestAnimationFrame(() => {
          this.updateAutocompletePosition();
        });
      }
    }, 0);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.inputRef && this.inputListenersAttached) {
      this.inputRef.removeEventListener('input', this.handleInputChange);
      this.inputRef.removeEventListener('keydown', this.handleInputKeyDown);
      this.inputListenersAttached = false;
    }
    // Clean up position tracking
    this.cleanupPositionTracking();
  }

  render() {
    const filteredDomains = this.getFilteredDomains();

    const noteIconUrl = 'https://marketing-static.signnow.com/341/images/home/emoji-tada.png';

    return (
      <form-handler-type-1 constants={this.constants} method="get" action={getFormAction()} submitHandler={this.handleSubmit} class="form-free-trial-type-1">
        <div class="form-free-trial-type-1__row">
          <div class="form-free-trial-type-1__cell">
            <div
              class={{
                'form-free-trial-type-1__input-wrapper': true,
                'form-free-trial-type-1__input-wrapper--autocomplete-visible': this.showAutocomplete,
              }}
            >
              <me-input
                type="email"
                name="email"
                placeholder={this.inputPlaceholder}
                required
                required-message={this.requiredMessage}
                invalid-message={this.invalidMessage}
                min-length={this.minLength}
                max-length={this.maxLength}
                min-length-message={this.minLengthMessage}
                max-length-message={this.maxLengthMessage}
              />
              {Build.isBrowser && filteredDomains.length > 0 && (
                <div
                  ref={el => {
                    if (!Build.isBrowser) return;
                    this.autocompleteRef = el as HTMLDivElement;
                    if (el) {
                      this.updateAutocompletePosition();
                    }
                  }}
                  class={`form-free-trial-type-1__autocomplete ${this.positionClass}`}
                  role="listbox"
                  id="email-autocomplete-listbox"
                  aria-label="Email domain suggestions"
                >
                  {filteredDomains.map((domain, index) => {
                    const atIndex = this.emailValue.lastIndexOf('@');
                    const beforeAt = atIndex >= 0 ? this.emailValue.substring(0, atIndex) : this.emailValue || '';
                    const optionId = `autocomplete-option-${index}`;
                    const isSelected = index === this.selectedIndex;
                    return (
                      <div
                        key={domain}
                        ref={el => {
                          if (el) {
                            this.optionRefs[index] = el;
                          }
                        }}
                        id={optionId}
                        role="option"
                        aria-selected={isSelected ? 'true' : 'false'}
                        tabIndex={this.showAutocomplete && isSelected ? 0 : -1}
                        class={{
                          'form-free-trial-type-1__autocomplete-item': true,
                          'form-free-trial-type-1__autocomplete-item--selected': isSelected,
                          'form-free-trial-type-1__autocomplete-item--focused': isSelected,
                        }}
                        onClick={() => this.selectDomain(domain)}
                        onMouseEnter={() => {
                          this.selectedIndex = index;
                          this.updateInputAriaAttributes();
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            this.selectDomain(domain);
                          }
                        }}
                      >
                        {beforeAt}@{domain}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <div class="form-free-trial-type-1__cell form-free-trial-type-1__cell--submit">
            <me-button tag="button" type="submit" variant={this.buttonVariant} class="form-free-trial-type-1__submit">
              {this.submitText}
            </me-button>
            {this.note && (
              <div class="form-free-trial-type-1__note">
                <img src={noteIconUrl} alt="" class="form-free-trial-type-1__note-icon" />
                <span class="form-free-trial-type-1__note-text">{this.note}</span>
              </div>
            )}
          </div>
        </div>
      </form-handler-type-1>
    );
  }
}
